"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * BRE-4647 - the floating-surface yield contract.
 *
 * A "floating surface" is a persistent, non-modal panel pinned over the app at
 * `z-floating` (1150) - today the open agent chat bubble and its composer. It
 * deliberately sits ABOVE `dialog` (1100) and `sheet` (1050) so a modal opened
 * from *elsewhere* on the page doesn't bury an agent the user explicitly opened
 * (the original BRE-4527 rule).
 *
 * That rule only ever considered overlays opened from *behind* the surface. An
 * overlay opened from *inside* it - the embedded goal card's quick-view sheet
 * (z-sheet), a check-in dialog (z-dialog) - lands below the very surface that
 * spawned it: invisible and unreachable. Both cases are real and they want
 * opposite stacking, so a single static z-index cannot satisfy both.
 *
 * The fix is provenance, not a bigger number: while an overlay from this
 * surface's own React subtree is open, the surface *yields* - drops to
 * `z-floating-yielded` (1045), below `sheet`/`dialog` - then returns to
 * `z-floating` when the overlay closes.
 *
 * Provenance comes free from React context, which propagates down the React
 * tree even though Radix DOM-portals overlay content to `document.body`. So
 * `DialogContent`/`SheetContent` rendered inside the bubble find this provider
 * and yield it; the same components rendered anywhere else find no provider and
 * are a no-op. Neither case has to know about the other.
 *
 * Why yield instead of raising the overlay above `floating`: `z-floating` is
 * shared by Popover, Select, DropdownMenu, ContextMenu, Tooltip and HoverCard,
 * which must stay above dialogs (a select inside a dialog). Raising dialog/sheet
 * past 1150 would put every one of those *underneath* the dialog containing
 * them - the exact collateral `z-alert` (1200) already carries, and which the
 * mobile bottom-sheet rule in `app/ui/global.css` documents as a known dead end.
 * Lowering the one surface that opted in touches nothing else in the scale.
 */

interface FloatingSurfaceYieldApi {
  /** Called by an overlay when it opens; returns its release fn. */
  acquire: () => () => void;
}

const FloatingSurfaceYieldContext =
  React.createContext<FloatingSurfaceYieldApi | null>(null);

/**
 * The yield count lives at MODULE scope, not in any one `<FloatingSurface>`'s
 * React state, and every mounted surface derives its stacking from it via
 * `useSyncExternalStore`.
 *
 * Why it cannot live in the component: a singleton overlay (the goal quick view)
 * can outlive the surface that opened it. The agent bubble unmounts its surface
 * entirely when it closes (`AgentChatBubble`: `if (mode !== "open") return null`),
 * but the quick-view sheet keeps rendering from the page's own elected instance.
 * If the count were per-instance, closing and re-opening the bubble would mount a
 * FRESH surface starting at 0 - so it would render on TOP of the still-open sheet.
 * A module-level count survives the unmount/remount: the re-opened surface reads
 * count > 0 and yields immediately. The quick view's yield is held by its elected
 * `<GoalQuickViewSheet />` instance for its rendered-and-open lifetime, so the
 * same count is released no matter which surface (if any) is mounted.
 *
 * The count is reference-counted (not a boolean) so overlapping overlays - a
 * dialog opened from inside the quick-view sheet - keep the surface yielded until
 * the LAST one closes.
 *
 * ⚠️ Single-surface invariant: this one module count yields ALL mounted
 * `<FloatingSurface>`s at once, so `isAnyFloatingSurfaceYielding()` can't say
 * WHICH surface an overlay came from. That is correct today because the only
 * floating surface is `AgentChatBubble` (BRE-4822 removed the compact peek
 * composer). If a future surface can coexist with the bubble, an overlay opened
 * inside surface A would wrongly yield surface B too - at which point this must
 * become a per-surface count keyed by surface identity rather than a single
 * module scalar.
 */
let floatingSurfaceYieldCount = 0;
const floatingSurfaceYieldListeners = new Set<() => void>();

function emitFloatingSurfaceYieldChange(): void {
  for (const listener of floatingSurfaceYieldListeners) listener();
}

/**
 * Increment the module yield count and return a single-use release. Exported so
 * the elected `<GoalQuickViewSheet />` instance - a singleton whose render site
 * is NOT inside the surface that opened it, so it cannot use the context path -
 * can hold the yield directly for its rendered-and-open lifetime.
 */
export function acquireFloatingSurfaceYield(): () => void {
  floatingSurfaceYieldCount += 1;
  emitFloatingSurfaceYieldChange();
  let released = false;
  return () => {
    // Guard double-release: acquire/release are symmetric across StrictMode's
    // dev double-invoke, but a caller that releases twice would otherwise drive
    // the count negative and pin every surface down.
    if (released) return;
    released = true;
    floatingSurfaceYieldCount -= 1;
    emitFloatingSurfaceYieldChange();
  };
}

function subscribeFloatingSurfaceYield(listener: () => void): () => void {
  floatingSurfaceYieldListeners.add(listener);
  return () => {
    floatingSurfaceYieldListeners.delete(listener);
  };
}

function isAnyFloatingSurfaceYielding(): boolean {
  return floatingSurfaceYieldCount > 0;
}

// `acquire` is a stable module-level fn, so the context value never changes -
// callers can safely list it in a dependency array.
const FLOATING_SURFACE_YIELD_API: FloatingSurfaceYieldApi = {
  acquire: acquireFloatingSurfaceYield,
};

/**
 * Yield the enclosing `<FloatingSurface>` (if any) for as long as the calling
 * component is mounted.
 *
 * ⚠️ Mount lifetime must equal OPEN lifetime, and for Radix that is a real
 * trap: `ui/sheet`'s `SheetContent` and `ui/dialog`'s `DialogContent` are our
 * own wrapper components, and they render whether or not the overlay is open -
 * the open-gating (`Presence`) lives *inside* the portal they return. Calling
 * this hook in the wrapper yields the surface permanently, from first render,
 * with nothing open. Call it only from a component mounted INSIDE the Radix
 * `Content` - which is what `<FloatingSurfaceYieldSentinel />` is for.
 *
 * A no-op outside a `<FloatingSurface>`, which is the overwhelming majority of
 * the app: overlays opened from a normal page must NOT disturb the bubble's
 * stacking.
 */
export function useYieldFloatingSurface(): void {
  const api = React.useContext(FloatingSurfaceYieldContext);

  // useLayoutEffect, not useEffect: the sentinel mounts in the same commit that
  // paints the overlay, so deferring the yield to a passive effect lets the
  // overlay paint one frame under the un-yielded surface - a visible stacking
  // flash. Layout effects run before paint, so the surface has already dropped.
  React.useLayoutEffect(() => {
    if (!api) return;
    return api.acquire();
  }, [api]);
}

/**
 * Renders nothing; yields the enclosing floating surface while mounted. Placed
 * inside `DialogPrimitive.Content` / `SheetPrimitive.Content` so every dialog
 * and sheet in the app is covered by construction - including ones that don't
 * exist yet - while staying inert outside a `<FloatingSurface>`.
 *
 * Must stay inside the Radix `Content` (see `useYieldFloatingSurface`); a
 * hand-rolled overlay that yields needs the same placement discipline.
 */
export function FloatingSurfaceYieldSentinel(): null {
  useYieldFloatingSurface();
  return null;
}

/**
 * A provenance probe for the SINGLETON case: "is the component calling me
 * rendered inside a `<FloatingSurface>`?"
 *
 * `useYieldFloatingSurface` covers the common case, where an overlay mounts when
 * it opens so mount lifetime *is* open lifetime. A SINGLETON overlay driven by a
 * store breaks that equivalence: exactly one instance renders it (elected by a
 * mount registry), so the render site is wherever that instance happens to live -
 * which is decided by mount order, not by who opened it. On the cadence event
 * detail page the page's own instance mounts at page load and wins the election,
 * so a card clicked inside the agent bubble rendered its sheet from the page's
 * instance, outside this surface, and the bubble never yielded (BRE-4647).
 *
 * Provenance of a singleton overlay is a property of the OPEN EVENT, not of the
 * render site, and only the opener knows it - it is the thing that genuinely sits
 * inside the surface. But the opener does NOT hold the yield: it opens the store
 * and unmounts, while the sheet outlives it. So the opener uses only the
 * NULLNESS of this return as provenance - non-null means "opened from inside a
 * surface" - and records that as a boolean on the store (`openedFromFloatingSurface`).
 * The elected `<GoalQuickViewSheet />` instance then holds the yield off that flag
 * via `acquireFloatingSurfaceYield`, for exactly as long as it is rendered-and-open,
 * so the yield is released by construction on close AND on navigation-away that
 * unmounts the sheet without a close event. The opener never calls the returned
 * `acquire` itself; the return type stays the `acquire` fn only so a future
 * mount==open caller could use it directly like the context path.
 *
 * Returns `null` outside a `<FloatingSurface>` - the same inert default the
 * context-based path has, so an opener rendered on a normal page is unaffected.
 * That fallback is load-bearing: the artifact openers (`ArtifactGoalRow`,
 * `ArtifactKpiCard`, ...) render BOTH inside the bubble and on the page behind
 * it, so they cannot know their own provenance and must read it from here.
 */
export function useFloatingSurfaceYieldAcquire(): (() => () => void) | null {
  const api = React.useContext(FloatingSurfaceYieldContext);
  // `acquire` is a stable module-level fn (the provider value never changes),
  // so this is a safe dependency for a caller's useCallback.
  return api?.acquire ?? null;
}

export interface FloatingSurfaceProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * A persistent panel pinned above the app that yields to overlays opened from
 * within it. Renders a plain `div` and owns its own z-index - do NOT pass a
 * `z-*` class in `className`, or it will fight the yield (tailwind-merge does
 * not dedupe these non-numeric custom tokens, so both would survive and the
 * winner would come down to stylesheet order).
 *
 * Overlays opt in automatically via the `ui/dialog` and `ui/sheet` primitives;
 * a hand-rolled overlay inside a floating surface must call
 * `useYieldFloatingSurface()` itself.
 */
export const FloatingSurface = React.forwardRef<
  HTMLDivElement,
  FloatingSurfaceProps
>(({ className, children, ...props }, ref) => {
  // Derived from the module-level count so a surface that mounts AFTER an overlay
  // opened it (a re-opened agent bubble over a still-open singleton sheet) yields
  // immediately, instead of starting fresh at 0 and covering the overlay.
  const isYielding = React.useSyncExternalStore(
    subscribeFloatingSurfaceYield,
    isAnyFloatingSurfaceYielding,
    () => false
  );

  return (
    <FloatingSurfaceYieldContext.Provider value={FLOATING_SURFACE_YIELD_API}>
      <div
        ref={ref}
        // The yield state as an unambiguous signal, asserted by
        // `floating-surface-yield.test.tsx` - not dead code. The class names
        // alone can't carry it: "z-floating-yielded" contains "z-floating", so
        // a substring check on className can't tell the two states apart.
        //
        // Not `aria-hidden`/`inert` while yielding: a yielded surface sits
        // behind a *non-modal* sheet (the goal quick view) that leaves the page
        // interactive, so the bubble legitimately stays usable underneath it.
        data-floating-surface-yielding={isYielding ? "true" : undefined}
        className={cn(isYielding ? "z-floating-yielded" : "z-floating", className)}
        {...props}
      >
        {children}
      </div>
    </FloatingSurfaceYieldContext.Provider>
  );
});
FloatingSurface.displayName = "FloatingSurface";
